import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import Loader from '../Loader'
import swal from 'sweetalert'
import { axiosInstance } from '../Config'
import { useNavigate, useParams } from 'react-router-dom'
import { usePermissionData } from '../../AppContext'

const ImageGallery = () => {
    const { kidKey } = useParams()
    const navigate = useNavigate()
    const [kid, setKid] = useState(null)
    const [images, setImages] = useState([])
    const [loading, setLoading] = useState(false)
    const [lightbox, setLightbox] = useState(null)

    useEffect(() => {
        window.scrollTo(0, 0)
        fetchGallery()
    }, [])

    const fetchGallery = async () => {
        setLoading(true)
        try {
            const res = await axiosInstance.get(`generated-images/${kidKey}`)
            const data = res.data?.data ?? res.data
            setKid(data.kid)
            const imgs = data.images ?? []
            setImages(imgs)
            document.title = `${data.kid?.username ?? 'Kid'}'s Gallery | Mindora`
        } catch {
            swal('', 'Failed to load gallery.', 'error')
        } finally {
            setLoading(false)
        }
    }

    const onDelete = (imageId) => {
        swal({ title: "Delete Image?", text: "This image will be permanently removed.", icon: "warning", buttons: true, dangerMode: true })
            .then(async (will) => {
                if (!will) return
                setLoading(true)
                try {
                    await axiosInstance.delete(`generated-images/${imageId}`)
                    swal("", "Image deleted successfully!", "success")
                    setImages(prev => prev.filter(img => img.id !== imageId))
                    if (lightbox?.id === imageId) setLightbox(null)
                } catch {
                    swal("", "Something went wrong!", "error")
                } finally {
                    setLoading(false)
                }
            })
    }

    return (
        <>
            {loading && <Loader />}
            <ContainerDashboard title={kid ? `${kid.username}'s Gallery` : "Gallery"} description="AI-generated images by this kid">
                <section className="ps-dashboards">
                    <div className="p-4 py-5 bg-gradientt rounded-4" style={{ minHeight: '75vh' }}>
                        <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded-4 flex-wrap"
                            style={{ background: 'linear-gradient(135deg, rgba(164,76,215,0.08), rgba(12,172,194,0.06))', border: '1px solid rgba(164,76,215,0.12)' }}>
                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 48, height: 48, background: 'linear-gradient(135deg, #A44CD7, #F55145)', color: '#fff', fontWeight: 700, fontSize: 18 }}>
                                {kid?.username?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div className="flex-grow-1">
                                <p className="fw-bold mb-0" style={{ color: '#1a1a2e' }}>
                                    {kid?.username ?? '—'}
                                    <span style={{ fontWeight: 400, color: '#999', fontSize: 13 }}> · Age {kid?.age}</span>
                                </p>
                                {kid?.parent?.name && (
                                    <p className="mb-0" style={{ fontSize: 13, color: '#666' }}>Parent: {kid.parent.name}</p>
                                )}
                            </div>
                            <span className="badge rounded-pill px-3 py-2"
                                style={{ background: 'rgba(164,76,215,0.12)', color: '#A44CD7', fontWeight: 600, fontSize: 13 }}>
                                {images.length} image{images.length !== 1 ? 's' : ''}
                            </span>
                            <button type="button" className="button flex-shrink-0"
                                style={{ background: '#1a1a2e', color: '#fff', borderRadius: '12px' }}
                                onClick={() => navigate(-1)}>
                                <i className="icon-arrow-left me-2"></i>Back
                            </button>
                        </div>
                        {images.length === 0 && !loading ? (
                            <div className="text-center py-5" style={{ color: '#aaa' }}>
                                <i className="bi bi-image" style={{ fontSize: 52, opacity: 0.3 }}></i>
                                <p className="mt-3 mb-0">No images found.</p>
                            </div>
                        ) : (
                            <div className="row g-4">
                                {images.map(img => (
                                    <div key={img.id} className="col-xl-3 col-lg-4 col-md-6 col-12">
                                        <ImageCard image={img} onView={() => setLightbox(img)} onDelete={() => onDelete(img.id)} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </ContainerDashboard>
            {lightbox && createPortal(
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
                    onClick={() => setLightbox(null)}>
                    <button onClick={() => setLightbox(null)}
                        style={{ position: 'absolute', top: 20, right: 24, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ✕
                    </button>
                    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: '90vw' }}>
                        <img src={lightbox.imageUrl} alt={lightbox.imageName}
                            style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.5)', display: 'block' }} />
                        <p className="mb-0 fw-semibold" style={{ color: '#fff', fontSize: 15 }}>{lightbox.imageName}</p>
                        <p className="mb-0" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
                            <i className="bi bi-calendar3 me-1"></i>{new Date(lightbox.createdAt || lightbox.uploadedAt).toLocaleDateString("en-GB")}
                        </p>
                    </div>
                </div>, document.body
            )}
        </>
    )
}

const ImageCard = ({ image, onView, onDelete }) => {
    const { checkAccess } = usePermissionData()
    return (
        <div className="rounded-4 overflow-hidden h-100 d-flex flex-column"
            style={{ background: '#fff', boxShadow: '0 2px 16px rgba(164,76,215,0.10)', border: '1px solid rgba(164,76,215,0.10)' }}>
            <div className="position-relative" style={{ height: 180, overflow: 'hidden', cursor: 'zoom-in', background: '#f0f2f5' }} onClick={onView}>
                {image.imageUrl ? (
                    <img src={image.imageUrl} alt={image.imageName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                        <i className="bi bi-image" style={{ fontSize: 48, color: '#ccc' }}></i>
                    </div>
                )}
                <div style={{ position: 'absolute', bottom: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, pointerEvents: 'none' }}>
                    <i className="bi bi-arrows-fullscreen"></i>
                </div>
            </div>
            <div className="d-flex flex-column p-3 flex-grow-1">
                <h6 className="fw-semibold mb-1 text-truncate" title={image.imageName} style={{ color: '#1a1a2e' }}>{image.imageName}</h6>
                <p className="text-muted mb-3" style={{ fontSize: '12px' }}>
                    <i className="bi bi-calendar3 me-1"></i>{new Date(image.createdAt || image.uploadedAt).toLocaleDateString("en-GB")}
                </p>
                {checkAccess(3, 4) && (
                    <div className="mt-auto">
                        <button type="button" className="btn w-100 fw-semibold"
                            style={{ background: '#EC2434', color: '#fff', borderRadius: '8px', padding: '5px 14px', fontSize: '13px' }}
                            onClick={onDelete}>
                            <i className="icon-trash me-1"></i> Delete
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ImageGallery